import { ChangeDetectionStrategy, Component, computed, effect, inject, output, signal, untracked, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonModule } from 'primeng/button';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { DateFieldDirective } from '../../directives/date-field.directive';
import { PopoverModule } from 'primeng/popover';
import { faChevronLeft, faChevronRight, faCircleInfo } from '@fortawesome/pro-duotone-svg-icons';
import { capitalizeFirst, displayDateFormatter } from '@branduals/date-display';
import { PrimeLocaleFeed } from '@branduals/primeng-locale';
import { BarTimelineComponent, TimelineBar } from '../charts/bar-timeline.component';
import { ChartViewSwitchComponent } from '../charts/chart-view-switch.component';
import { DatepickerEscapeTagDirective } from '../../directives/datepicker-escape-tag.directive';
import { InlineCalendarKeysDirective } from '../../directives/inline-calendar-keys.directive';
import { TemporalActivityService } from '../../services/temporal-activity.service';
import { TemporalContextService } from '../../services/temporal-context.service';
import { LocaleService } from '../../services/locale.service';
import { BrandChangeActivityResult } from '../../graphql/temporal-operations';
import { dayToInstant, monthsBack } from '../../utils/temporal-date.utils';
import { pluralKey } from '../../utils/plural.utils';
import { DisplayDatePipe } from '../../pipes/display-date.pipe';

type StripState = 'idle' | 'loading' | 'ready' | 'failed';

/** A calendar month: the year and the month's index from zero, as a `Date` and PrimeNG's calendar both count it. */
interface ShownMonth { year: number; month: number }

/** The bar of the month the calendar shows. */
const CURRENT_BAR_COLOR = 'var(--p-primary-color)';
/**
 * Every other bar: a neutral, so the brand colour marks one month only. Mixed as quiet as a data mark may be: it
 * keeps 3 : 1 against the panel in both themes (3.1 : 1 on the light surface, 4.4 : 1 on the dark one).
 */
const QUIET_BAR_COLOR = 'color-mix(in srgb, var(--p-text-secondary-color) 70%, transparent)';

const monthOf = (day: Date): ShownMonth => ({ year: day.getFullYear(), month: day.getMonth() });
/** A month as one number, so two months compare and step by plain arithmetic. */
const ordinalOf = ({ year, month }: ShownMonth): number => year * 12 + month;
const monthAt = (ordinal: number): ShownMonth => ({ year: Math.floor(ordinal / 12), month: ((ordinal % 12) + 12) % 12 });

let temporalPanelUid = 0;

/**
 * The "Time travel" panel body — hosted in the
 * topbar's slide panel. Change-density strip + inline day picker +
 * presets + honesty note + active state + Go. The strip is a navigation aid inside
 * a picker, NOT an Analytics surface (Intelligence-charter seam).
 * Change-density-strip failure degrades to picker-only and never blocks mode entry.
 *
 * The strip shows ONE YEAR — the year of the month the calendar is on — as twelve named months, and the two move
 * together: a bar or a year arrow turns the calendar, and the calendar's own navigation turns the strip.
 */
@Component({
    selector: 'app-temporal-panel',
    standalone: true,
    imports: [DisplayDatePipe, FormsModule, TranslateModule, FontAwesomeModule, ButtonModule, DatePickerModule, DateFieldDirective, PopoverModule, BarTimelineComponent, ChartViewSwitchComponent, DatepickerEscapeTagDirective, InlineCalendarKeysDirective],
    templateUrl: './temporal-panel.component.html',
    styleUrl: './temporal-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemporalPanelComponent {
    protected readonly temporal = inject(TemporalContextService);
    private readonly activityService = inject(TemporalActivityService);
    private readonly localeService = inject(LocaleService);
    private readonly translate = inject(TranslateService);
    private readonly primeLocale = inject(PrimeLocaleFeed);

    /** Fired after a mode navigation (Go / Back to today) — the topbar closes the panel. */
    readonly entered = output<void>();

    protected readonly faCircleInfo = faCircleInfo;
    protected readonly faChevronLeft = faChevronLeft;
    protected readonly faChevronRight = faChevronRight;

    private readonly uid = temporalPanelUid++;
    /** The id of the quick picks' label, so the group takes its name from the words a member sees above it. */
    protected readonly quickPicksLabelId = `temporal-quick-picks-label-${this.uid}`;
    /** The id of the strip's label: the chart ↔ table switch beside it takes its name from it. */
    protected readonly stripLabelId = `temporal-strip-label-${this.uid}`;

    /** Chart ↔ table flip for the density strip — the switch lives in the panel's STATIC
     *  strip-header row (QA round 2, 2026-07-14: the chart-internal toggle rode the scroll). */
    protected readonly stripTable = signal(false);

    private readonly picker = viewChild(DatePicker);

    protected readonly state = signal<StripState>('idle');
    protected readonly activity = signal<BrandChangeActivityResult | null>(null);
    protected readonly selectedDay = signal<Date | null>(null);
    /** Calendar view seed (bar click) — drives p-datepicker [defaultDate]; NEVER the model. */
    protected readonly viewDate = signal<Date | null>(null);

    /**
     * Today — the picker's max bound and the last month the strip reaches. Read again at every open: the panel
     * stays mounted for the life of the page, and a day fixed at its creation left the new day unpickable after
     * midnight.
     */
    protected readonly today = signal(new Date());

    /**
     * The month the calendar shows. The panel sets it wherever it turns the calendar itself (a bar, a year arrow, a
     * preset) and reads it back from the calendar after every gesture inside it — see `syncShownMonth`.
     */
    protected readonly shownMonth = signal<ShownMonth>(monthOf(new Date()));

    /** The bar under the pointer or the keyboard, by its month's index — the caption names it while it lasts. */
    private readonly previewedMonth = signal<number | null>(null);

    /** Picker min bound = brand epoch; absent while degraded (no min-bound). */
    protected readonly minDate = computed(() => {
        const activity = this.activity();
        return this.state() === 'ready' && activity ? new Date(activity.historySince) : null;
    });

    protected readonly canGo = computed(() => this.selectedDay() !== null);

    /**
     * The months the history reaches, first and last, each as one number. The server's months and the picker's
     * two bounds are read together, so a month the calendar lets a day be picked in is never drawn as out of reach.
     */
    private readonly reach = computed<{ first: number; last: number; busiest: number; totals: Map<number, number> } | null>(() => {
        const activity = this.activity();
        if (!activity || activity.months.length === 0) return null;
        const totals = new Map(activity.months.map(m => [ordinalOf({ year: m.year, month: m.month - 1 }), m.revisions + m.uploads + m.moves]));
        const served = [...totals.keys()];
        return {
            first: Math.min(...served, ordinalOf(monthOf(new Date(activity.historySince)))),
            last: Math.max(...served, ordinalOf(monthOf(this.today()))),
            busiest: Math.max(...totals.values()),
            totals,
        };
    });

    /**
     * The language the panel's own labels are built in: the one whose catalog is in hand — the language the
     * component library has been handed — and the language setting until the first catalog lands. The setting
     * changes BEFORE its catalog arrives, and a label built at that moment kept the previous language's sentence, or
     * showed a raw key, until the calendar's month was turned.
     */
    private readonly labelLanguage = computed(() => this.primeLocale.language() ?? this.localeService.activeUILanguage());

    /**
     * The chart's page: the twelve months of the shown year → TimelineBar[] + a parallel caption per bar.
     * Reads the label language FIRST so a language switch recomputes the
     * `instant()`-built labels (playbook §4 frozen-instant rule).
     */
    private readonly yearPage = computed<{ bars: TimelineBar[]; captions: string[] }>(() => {
        const lang = this.labelLanguage();
        const reach = this.reach();
        if (!reach) {
            return { bars: [], captions: [] };
        }
        const shown = this.shownMonth();
        const summary = (month: string, changes: number): string => this.translate.instant('temporal.panel.monthSummary', {
            month,
            changes: this.translate.instant(pluralKey('temporal.panel.changesCount', changes, lang), { count: changes }),
        }) as string;

        const initial = new Intl.DateTimeFormat(lang, { month: 'narrow' });
        const name = new Intl.DateTimeFormat(lang, { month: 'long' });
        const nameWithYear = displayDateFormatter(lang, 'monthYear');
        const bars: TimelineBar[] = [];
        const captions: string[] = [];
        for (let month = 0; month < 12; month++) {
            const start = new Date(shown.year, month, 1);
            const ordinal = ordinalOf({ year: shown.year, month });
            const total = reach.totals.get(ordinal) ?? 0;
            const current = month === shown.month;
            const monthName = capitalizeFirst(name.format(start), lang);
            bars.push({
                label: initial.format(start),
                tableLabel: monthName,
                value: total,
                color: current ? CURRENT_BAR_COLOR : QUIET_BAR_COLOR,
                srLabel: summary(capitalizeFirst(nameWithYear.format(start), lang), total),
                current,
                outOfRange: ordinal < reach.first || ordinal > reach.last,
            });
            captions.push(summary(monthName, total));
        }
        return { bars, captions };
    });

    /**
     * The list view's rows: every month the history reaches, NEWEST first, each named with its year — and, beside
     * them, the month each row stands for, so a row turns the calendar as a bar does. The list is read by itself (no
     * chart is drawn beside it), so it holds the whole history, where the chart pages through it a year at a time.
     * Newest first, as a history reads: the months a member came for stand at the top of a box that scrolls, with
     * nothing to scroll into place — listed oldest first, the box had to be moved to its end at every open.
     */
    private readonly history = computed<{ rows: TimelineBar[]; months: Map<TimelineBar, ShownMonth> }>(() => {
        const lang = this.labelLanguage();
        const reach = this.reach();
        const rows: TimelineBar[] = [];
        const months = new Map<TimelineBar, ShownMonth>();
        if (!reach) {
            return { rows, months };
        }
        const nameWithYear = displayDateFormatter(lang, 'monthYear');
        for (let ordinal = reach.last; ordinal >= reach.first; ordinal--) {
            const shown = monthAt(ordinal);
            const row: TimelineBar = {
                label: capitalizeFirst(nameWithYear.format(new Date(shown.year, shown.month, 1)), lang),
                value: reach.totals.get(ordinal) ?? 0,
                color: QUIET_BAR_COLOR,
            };
            rows.push(row);
            months.set(row, shown);
        }
        return { rows, months };
    });

    /** What the chart component is handed: the shown year for the chart, the whole history for the list view. */
    protected readonly bars = computed(() => this.stripTable() ? this.history().rows : this.yearPage().bars);

    /** The busiest month of the whole history fills the strip's height, so a quiet year reads as quiet. */
    protected readonly scaleMax = computed(() => this.reach()?.busiest ?? null);

    /** The month the caption names: the bar under the pointer or the keyboard, else the calendar's month. */
    protected readonly caption = computed(() => {
        const { bars, captions } = this.yearPage();
        const month = this.previewedMonth() ?? this.shownMonth().month;
        return bars[month] && !bars[month].outOfRange ? captions[month] : '';
    });

    /**
     * Every caption the shown year can show. They are laid out, unseen, in the caption's own box under the one in
     * view, so the box is as wide as the widest of them: naming the bar under the pointer then never changes the
     * header row's width, and the row never wraps and unwraps under a moving pointer.
     */
    protected readonly captions = computed(() => {
        const { bars, captions } = this.yearPage();
        return captions.filter((_, month) => !bars[month].outOfRange);
    });

    protected readonly canStepBack = computed(() => {
        const reach = this.reach();
        return !!reach && this.shownMonth().year > monthAt(reach.first).year;
    });

    protected readonly canStepForward = computed(() => {
        const reach = this.reach();
        return !!reach && this.shownMonth().year < monthAt(reach.last).year;
    });

    constructor() {
        // The calendar is mounted for the life of the page, and a language can arrive under it: at a switch, and at
        // boot when the catalog lands after the first render. The library then re-labels the mounted calendar's
        // weekday row and leaves its day grid as it was — every day under the weekday of the PREVIOUS week start
        // until the month is turned. So whenever the library has been handed a language, the calendar is turned to
        // the month it already shows, which lays the days out again and keeps the member where they were. Keyed on
        // the feed, never on the language setting: the setting changes before the catalog arrives, and a turn made
        // then would lay the grid out under the old week start.
        effect(() => {
            if (this.primeLocale.language() === null) return;
            untracked(() => this.showMonth(this.shownMonth()));
        });
    }

    /**
     * Runs at every panel open (the topbar calls it — notifications-panel precedent): reads today again, fetches the
     * change-density strip, and — while a past day is viewed — turns the calendar to that day's month. The panel
     * names the viewed day right above the calendar, and a calendar standing on another month under it (today's,
     * after a reload or an arrival by link) contradicted that line. The viewed day is NOT made the pick: a view
     * entered from an item's history is an exact moment, and a pick would move it to the end of that day.
     */
    async load(): Promise<void> {
        if (this.state() === 'loading') return;
        this.today.set(new Date());
        const viewed = this.temporal.active() ? this.temporal.asOf() : null;
        if (viewed) {
            this.showMonth(monthOf(viewed));
        }
        this.state.set('loading');
        try {
            this.activity.set(await this.activityService.getBrandChangeActivity());
            this.state.set('ready');
        } catch (error) {
            // The strip degrades to picker-only; entry is never blocked.
            console.warn('brandChangeActivity failed — degrading to picker-only', error);
            this.activity.set(null);
            this.state.set('failed');
        }
    }

    /** A bar, or a row of the list, pre-positions the calendar on its month; the member still picks the day. */
    protected onBarSelect(bar: TimelineBar): void {
        const listed = this.history().months.get(bar);
        if (listed) {
            this.showMonth(listed);
            return;
        }
        const month = this.yearPage().bars.indexOf(bar);
        if (month >= 0) {
            this.showMonth({ year: this.shownMonth().year, month });
        }
    }

    protected onBarPreview(bar: TimelineBar | null): void {
        const month = bar ? this.yearPage().bars.indexOf(bar) : -1;
        this.previewedMonth.set(month >= 0 ? month : null);
    }

    /** A year arrow: the same month a year away, held inside the history (its first or last month past either end). */
    protected stepYear(years: number): void {
        const reach = this.reach();
        if (!reach) return;
        const target = ordinalOf(this.shownMonth()) + years * 12;
        this.showMonth(monthAt(Math.min(Math.max(target, reach.first), reach.last)));
    }

    /**
     * The calendar's month, read from the calendar after a gesture inside it. Its own outputs do not cover every
     * way the shown month changes (a year arrow in its month view emits nothing, and picking a year reports the old
     * month), so the panel reads the two fields the calendar itself draws from.
     */
    protected syncShownMonth(): void {
        const picker = this.picker();
        if (!picker) return;
        const shown = this.shownMonth();
        if (picker.currentYear !== shown.year || picker.currentMonth !== shown.month) {
            this.shownMonth.set({ year: picker.currentYear, month: picker.currentMonth });
        }
    }

    /** Presets COMMIT the day (Go stays the single entry gesture), clamped to the epoch day. */
    protected applyPreset(months: number): void {
        let day = monthsBack(new Date(), months);
        const min = this.minDate();
        if (min) {
            const epochDay = new Date(min.getFullYear(), min.getMonth(), min.getDate());
            if (day.getTime() < epochDay.getTime()) {
                day = epochDay;
            }
        }
        this.selectedDay.set(day);
        this.viewDate.set(day);
        this.shownMonth.set(monthOf(day));
    }

    protected async go(): Promise<void> {
        const day = this.selectedDay();
        if (!day) return;
        // The emit closes the panel — only a transition that actually happened may close it.
        // A falsy resolve is a refusal (the member kept editing at the unsaved-work prompt),
        // and the panel must stay exactly where they left it, the pick included.
        if (await this.temporal.enter(dayToInstant(day))) {
            // A pick is a choice not yet acted on. Once acted on it is the viewed day, which the panel names in
            // words; left standing, it stayed lit at the next open with the button live for a day already left.
            this.selectedDay.set(null);
            this.entered.emit();
        }
    }

    protected async backToToday(): Promise<void> {
        if (await this.temporal.exit()) {
            this.selectedDay.set(null);
            this.entered.emit();
        }
    }

    /**
     * Turns the calendar to a month. A NEW date each time: the calendar takes its month from the input only when the
     * value changes, so handing it the same object again after it had walked away by its own arrows moved nothing.
     */
    private showMonth(month: ShownMonth): void {
        this.viewDate.set(new Date(month.year, month.month, 1));
        this.shownMonth.set(month);
    }
}
